import {
  ArrowDown, ArrowRight, ArrowUp, Calendar, Check, ChevronDown, ChevronUp, Clock, ExternalLink, Info,
  Star,
  TrendingUp,
} from 'react-feather';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';

import { Locale } from '../../__generated__/graphql';

import { Container } from '../Common/Layout';
import { EmbedContext } from '../Common/EmbedProvider';
import { Event, Review } from '../Checkout/helpers';
import { EventHeader, EventInfo } from '../../common/Common/EventHeader';
import { PracticalInfoIcon, getPracticalInfoFactTitle } from '../../common/practicalInfo';
import { Theme } from '../../theme';
import { defaultSecondary } from '../../theme/variables';
import { getUrlWithoutPrefix, isEmpty, resizedImageUrl, toggleInList } from '../../common/helpers';
import { reviewsPerPage, useEvent } from '../Common/EventProvider';
import { useTracking } from '../../common/Tracking';
import GetEventReviewsQuery from './GetEventReviewsQuery';
import PopularEventLabel from '../../common/PopularEventLabel';
import PriceRange from '../../common/Common/PriceRange';
import ProgressBar from '../../common/ProgressBar';
import ShowMoreToggle from '../../common/ShowMoreToggle';
import SupportaSymbol from '../../common/Common/SupportaSymbol';
import UI from '../../common/UI';
import config from '../config';
import useAutoScrollToSection from '../Common/useAutoScrollToSection';
import useCheckoutUrlState, { CheckoutUrlState } from '../Checkout/useCheckoutUrlState';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useQuery from '../../api/useQuery';
import useStickyElement from '../../common/useStickyElement';

const AboutPage = () => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const { embedded } = useContext(EmbedContext);
  const [urlState] = useCheckoutUrlState();
  const device = useMediaDevice();
  const { track } = useTracking();

  const { event } = useEvent();

  useDocumentTitle([event.title, t('common:atleta')]);

  useAutoScrollToSection();

  const hasTickets = event.ticket_categories.some((ticketCategory) => (
    ticketCategory.tickets_for_sale.length > 0
  ));

  const usps = event.usp_facts.map((fact) => fact.title).filter((title) => title);

  const eventsPerPage = 5;
  const [eventsLimit, setEventsLimit] = useState(eventsPerPage);
  const visibleEvents = event.related_events.slice(0, eventsLimit);

  const containsTicketsUrlParam = [
    'paymentStatus',
    'showCategories',
    'ticketCategoryIds',
    'coupon',
    'invitationCode',
    'charityId',
  ].some((param) => !isEmpty(urlState[param as keyof CheckoutUrlState]));

  useEffect(() => {
    track?.('EventPage:Opened');
  }, [track]);

  if (containsTicketsUrlParam) {
    return <Redirect to={`/${event.id}/tickets${window.location.search}`} />;
  }

  return (
    <>
      {(event.review_average_rating > 0 || event.trend_score >= 60) && (
        <UI.FlexContainer sc={{ gap: 0.5, alignItems: 'center' }}>
          <UI.Delimit delimiter={<UI.Div style={{ height: 12, width: 1 }} sc={{ background: 'gray.250' }} />}>
            {event.review_average_rating > 0 && (
              <>
                <UI.Div>
                  <UI.Icon>
                    <Star fill="currentColor" />
                  </UI.Icon>
                  {' '}
                  <UI.Strong>
                    {formatNumber(event.review_average_rating, {
                      minimumFractionDigits: 2, maximumFractionDigits: 2,
                    })}
                  </UI.Strong>
                </UI.Div>
                <UI.Div style={{ height: 12, width: 1 }} sc={{ background: 'gray.250' }} />
                <UI.Link to={`/${event.id}?section=Reviews`} sc={{ secondary: true }}>
                  {t('event_info.n_reviews', {
                    count: event.reviews.total, value: formatNumber(event.reviews.total),
                  })}
                </UI.Link>
              </>
            )}
            {event.trend_score >= 60 && (
              <PopularEventLabel sc={{ small: true }}>
                <UI.Icon>
                  <TrendingUp style={{ strokeWidth: 2.5 }} />
                </UI.Icon>
                {' '}
                <UI.Span>
                  {t('event_info.popular')}
                </UI.Span>
              </PopularEventLabel>
            )}
          </UI.Delimit>
        </UI.FlexContainer>
      )}

      {usps.length > 0 && (
        <UI.Card
          sc={{
            background: 'secondary.500/5',
            px: [4, embedded ? 3 : 7],
          }}
        >
          <UI.GridContainer
            sc={{ columns: ['1fr', usps.map(() => '1fr').join(' 1px ')], gutter: [0.75, 0.5], alignVertical: 'center' }}
          >
            {usps.map((usp, index) => (
              <Fragment key={index}>
                {index > 0 && (
                  <UI.Div
                    sc={{ background: 'secondary.200' }}
                    style={{
                      width: device.isSmall ? 30 : 1,
                      height: device.isSmall ? 1 : '100%',
                      margin: '0 auto',
                    }}
                  />
                )}
                <UI.Div
                  sc={{ fontWeight: 500, textAlign: 'center' }}
                  style={{ textWrap: 'balance' }}
                >
                  {usp}
                </UI.Div>
              </Fragment>
            ))}
          </UI.GridContainer>
        </UI.Card>
      )}

      {event.description && (
        <UI.HTML html={event.description} />
      )}

      {!event.description && (
        <UI.Div sc={{ color: 'gray.600' }}>
          {t('event_info.no_description_yet')}
        </UI.Div>
      )}

      {(event.registration_opens_at || hasTickets || event.enable_interest_list) && (
        <StickyRegisterButton />
      )}

      {hasTickets && (
        <>
          <UI.HR />

          <TicketsSection />
        </>
      )}

      {event.whats_included_facts.length > 0 && (
        <>
          <UI.HR />

          <WhatsIncludedSection />
        </>
      )}

      {event.practical_info_facts.length > 0 && (
        <>
          <UI.HR />

          <PracticalInformationSection />
        </>
      )}

      {event.enable_fundraising && event.fundraising_campaigns_count > 0 && (
        <>
          <UI.HR />

          <FundraisingSection />
        </>
      )}

      {event.reviews.total > 0 && (
        <>
          <UI.HR />

          <ReviewsSection />
        </>
      )}

      <UI.HR />

      <OrganizerSection />

      {event.related_events.length > 0 && (
        <>
          <UI.HR />

          <UI.GridContainer sc={{ gutter: 0.75 }}>
            <UI.H3>
              {t('event_info.related_events')}
            </UI.H3>

            <UI.GridContainer sc={{ gutter: 0.75 }}>
              {visibleEvents.map((relatedEvent) => (
                <UI.LinkBlock to={`/${relatedEvent.id}`} title={relatedEvent.title} key={relatedEvent.id}>
                  <UI.GridContainer sc={{ columns: '72px 1fr', gutter: 0.75, alignVertical: 'center' }}>
                    <EventHeader
                      headerUrl={relatedEvent.header?.file.url}
                      size={900}
                      sportType={relatedEvent.ticket_categories.filter(
                        (ticketCategory) => ticketCategory.sport_type,
                      )[0]?.sport_type}
                      aspectRatio={1.25}
                      sc={{ padding: 0, borderRadius: 'xl' }}
                      color={relatedEvent.brand_color || defaultSecondary}
                      alt={relatedEvent.title}
                    />
                    <UI.Div style={{ minWidth: 0 }}>
                      <UI.H5 sc={{ fontWeight: 600 }}>
                        <UI.Truncate>
                          {relatedEvent.title}
                        </UI.Truncate>
                      </UI.H5>
                      <EventInfo
                        event={{ ...relatedEvent, location: { ...relatedEvent.location, id: null } }}
                        direction="column"
                      />
                    </UI.Div>
                  </UI.GridContainer>
                </UI.LinkBlock>
              ))}
            </UI.GridContainer>

            {visibleEvents.length < event.related_events.length && (
              <UI.FlexContainer sc={{ justifyContent: 'center' }}>
                <UI.Button
                  onClick={() => setEventsLimit((limit) => limit + eventsPerPage)}
                  sc={{ borderRadius: 'xl', size: 'sm', outline: true }}
                >
                  <UI.Icon>
                    <ArrowDown />
                  </UI.Icon>
                  {' '}
                  {t('event_info.show_more')}
                </UI.Button>
              </UI.FlexContainer>
            )}

            {visibleEvents.length > eventsPerPage && visibleEvents.length >= event.related_events.length && (
              <UI.FlexContainer sc={{ justifyContent: 'center' }}>
                <UI.Button
                  onClick={() => setEventsLimit(eventsPerPage)}
                  sc={{ borderRadius: 'xl', size: 'sm', outline: true }}
                >
                  <UI.Icon>
                    <ArrowUp />
                  </UI.Icon>
                  {' '}
                  {t('event_info.show_less')}
                </UI.Button>
              </UI.FlexContainer>
            )}
          </UI.GridContainer>
        </>
      )}
    </>
  );
};

export const StickyRegisterButton = () => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { parseDate, formatDate } = useLocale();
  const { embedded } = useContext(EmbedContext);
  const device = useMediaDevice();

  const hasTickets = event.ticket_categories.some((ticketCategory) => (
    ticketCategory.tickets_for_sale.length > 0
  ));

  const { anchorRef, elementRef, isSticky, position } = useStickyElement({
    scrollContainerId: embedded && !device.isSmall ? 'PageContainer' : undefined,
    position: 'bottom',
  });

  const theme = useTheme() as Theme;

  return (
    <UI.Div ref={anchorRef}>
      <UI.Div
        ref={elementRef}
        style={{
          bottom: isSticky ? (embedded && !device.isSmall ? 2.5 : 0.75) * theme.gutter : undefined,
          left: isSticky ? position.left : undefined,
          right: isSticky ? position.right : undefined,
          position: isSticky ? 'fixed' : 'relative',
          zIndex: 300,
        }}
      >
        <Container sc={{ py: 0, px: isSticky ? undefined : 0 }}>
          <UI.FadeIn sc={{ when: isSticky }}>
            <RegisterButton
              to={`/${event.id}/${(hasTickets || event.registration_opens_at) ? 'tickets' : 'interest'}?section=EventTitle`}
              sc={{
                brand: isSticky ? 'secondary/90' : 'secondary',
                size: 'xl',
                block: true,
              }}
            >
              {event.registration_opens_at ? (
                <>
                  <UI.Icon>
                    <Clock />
                  </UI.Icon>
                  {' '}
                  {t('event_info.registration_opens_at', {
                    time: formatDate(
                      parseDate(event.registration_opens_at, { timezone: 'UTC' }),
                      // Assumes the timestamp is only present if registration opens in < 1 hour.
                      { format: 'display_time', timezone: null },
                    ),
                  })}
                </>
              ) : (
                <>
                  {hasTickets ? t('event_info.register') : t('interest_list.newsletter')}
                  {' '}
                  <UI.Icon>
                    <ArrowRight />
                  </UI.Icon>
                </>
              )}
            </RegisterButton>
          </UI.FadeIn>
        </Container>
      </UI.Div>
    </UI.Div>
  );
};

export const TicketsSection = () => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { parseDate, formatDate } = useLocale();

  const ticketCategories = event.ticket_categories.filter(
    (ticketCategory) => ticketCategory.tickets_for_sale.length > 0,
  );

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.H3>
        {t('event_info.tickets')}
      </UI.H3>

      <UI.GridContainer sc={{ gutter: 0.5 }}>
        {ticketCategories.map((ticketCategory) => (
          <UI.NavCard
            to={`/${event.id}/tickets?showCategories[]=${ticketCategory.id}&section=EventTitle`}
            title={ticketCategory.title}
            sc={{ spacing: 'sm' }}
            key={ticketCategory.id}
          >
            <UI.GridContainer sc={{ columns: '1fr min-content', gutter: 0.25 }}>
              <UI.Div>
                <UI.H4>
                  {ticketCategory.title}
                </UI.H4>
                <UI.Div>
                  <PriceRange
                    prices={ticketCategory.tickets_for_sale.reduce(
                      (promotions, ticket) => [
                        ...promotions,
                        ...ticket.promotions_for_sale.map((promotion) => ({
                          ...promotion, amount: promotion.current_amount,
                        })),
                      ],
                      [],
                    )}
                  />
                </UI.Div>
              </UI.Div>
              <UI.Div>
                <UI.LinkButton
                  to={`/${event.id}/tickets?showCategories[]=${ticketCategory.id}&section=EventTitle`}
                  sc={{ size: 'sm', brand: 'secondary' }}
                >
                  {t('selector.choose')}
                  {' '}
                  <UI.Icon>
                    <ArrowRight />
                  </UI.Icon>
                </UI.LinkButton>
              </UI.Div>
            </UI.GridContainer>
            <UI.FlexContainer sc={{ gap: 0.75, wrap: 'wrap', mt: 1, color: 'gray.500' }}>
              {ticketCategory.sport_type && ticketCategory.sport_type !== 'other' && (
                <UI.Label sc={{ small: true }}>
                  {t(`common:sport_types.${ticketCategory.sport_type}`)}
                </UI.Label>
              )}
              {ticketCategory.formatted_distances.length > 0 && (
                <UI.Span sc={{ fontWeight: 500, small: true }}>
                  <UI.Icon>
                    <UI.Icons.Course />
                  </UI.Icon>
                  {' '}
                  <UI.Delimit>
                  {...ticketCategory.formatted_distances}
                  </UI.Delimit>
                </UI.Span>
              )}
              {ticketCategory.start_date && (
                <UI.Span sc={{ fontWeight: 500, small: true }}>
                  <UI.Icon>
                    <Calendar />
                  </UI.Icon>
                  {' '}
                  {formatDate(
                    parseDate(ticketCategory.start_date, { timezone: 'UTC' }),
                    { format: 'display_date_without_year' },
                  )}
                </UI.Span>
              )}
            </UI.FlexContainer>
          </UI.NavCard>
        ))}
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

type WhatsIncludedFact = Event['whats_included_facts'][number];

export const WhatsIncludedSection = () => {
  const { t } = useTranslation();
  const { event } = useEvent();

  const [fact, setFact] = useState<WhatsIncludedFact | null>(null);

  const [openModal, closeModal] = useModal(() => (
    fact && (
      <UI.Modal close={() => setFact(null)}>
        <UI.H4 sc={{ mb: 1 }}>
          {fact.title}
        </UI.H4>
        <UI.NL2BR content={fact.description} />
      </UI.Modal>
    )
  ), [fact]);

  useEffect(() => {
    if (fact) {
      openModal();
    } else {
      closeModal();
    }
  }, [fact, openModal, closeModal]);

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.H3>
        {t('event_info.whats_included')}
      </UI.H3>

      <UI.FlexContainer sc={{ gap: 1 / 3, wrap: 'wrap' }}>
        {event.whats_included_facts.map((fact) => (
          <UI.Label
            sc={{ brand: 'secondary.300/15', color: 'gray.800', px: 1.5, py: 0.75 }}
            onClick={fact.description ? () => setFact(fact) : undefined}
            key={fact.id}
          >
            <UI.Icon sc={{ color: 'secondary', size: 1.25 }}>
              <Check />
            </UI.Icon>
            {' '}
            {fact.title}
            {fact.description && (
              <UI.Icon sc={{ ml: 1 }}>
                <Info />
              </UI.Icon>
            )}
          </UI.Label>
        ))}
      </UI.FlexContainer>
    </UI.GridContainer>
  );
};

export const PracticalInformationSection = () => {
  const { t } = useTranslation();
  const { event, practicalInfoTopics = [] } = useEvent();

  const [openFactIds, setOpenFactIds] = useState<string[]>([]);

  const toggleFact = (factId: string) => setOpenFactIds((factIds) => toggleInList(factIds, factId));

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }} id="PracticalInfo">
      <UI.H3>
        {t('event_info.practical_info')}
      </UI.H3>

      <UI.Card sc={{ background: 'transparent', outline: 'gray.200', hasPanels: true }}>
        {event.practical_info_facts.map((fact) => {
          const open = openFactIds.includes(fact.id);

          return (
            <UI.Div sc={{ py: 3 }} key={fact.id}>
              <UI.LinkBlock
                onClick={() => toggleFact(fact.id)}
                sc={{ padding: 3, margin: -3 }}
                title={getPracticalInfoFactTitle(fact.title, practicalInfoTopics, t)}
              >
                <UI.GridContainer sc={{ columns: '14px 1fr 21px', gutter: 0.5 }}>
                  <UI.Div>
                    <UI.Icon sc={{ color: 'gray.500', mb: 0 }}>
                      <PracticalInfoIcon topic={fact.title} />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.H4 sc={{ py: 1 / 3 }}>
                      {getPracticalInfoFactTitle(fact.title, practicalInfoTopics, t)}
                    </UI.H4>
                    <UI.AnimateHeight isVisible={open}>
                      <UI.Div sc={{ mt: 3, relative: true, zIndex: 301 }}>
                        <UI.HTML html={fact.description} />
                      </UI.Div>
                    </UI.AnimateHeight>
                  </UI.Div>
                  <UI.Div>
                    <UI.Icon sc={{ color: 'gray.500', size: 1.5 }}>
                      {open ? <ChevronUp /> : <ChevronDown />}
                    </UI.Icon>
                  </UI.Div>
                </UI.GridContainer>
              </UI.LinkBlock>
            </UI.Div>
          );
        })}
      </UI.Card>
    </UI.GridContainer>
  );
};

interface ReviewsSectionProps {
  perPage?: number;
}

export const ReviewsSection = ({ perPage = reviewsPerPage }:ReviewsSectionProps) => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { formatNumber } = useLocale();

  const [limit, setLimit] = useState(perPage);
  const [reviews, setReviews] = useState(event.reviews);

  const { loading } = useQuery(GetEventReviewsQuery, {
    variables: {
      id: event.id,
      limit,
    },
    skip: limit <= perPage,
    onCompleted: (data) => {
      setReviews(data.event.reviews);
    },
  });

  const showMoreReviews = () => {
    setLimit((limit) => limit + perPage);
  };

  return (
    <UI.GridContainer id="Reviews" sc={{ gutter: 0.75 }}>
      <UI.H3>
        {t('event_info.reviews')}
        <UI.Badge sc={{ ml: 1, small: true }}>
          {formatNumber(reviews.total)}
        </UI.Badge>
      </UI.H3>

      <UI.Div>
        <UI.H4 sc={{ mb: 2 }}>
          {t('event_info.overall_rating')}
        </UI.H4>

        <UI.Div sc={{ fontSize: 5 }}>
          <UI.Icon sc={{ mb: 1.125 }}>
            <Star fill="currentColor" />
          </UI.Icon>
          {' '}
          <UI.Span sc={{ fontWeight: 600 }}>
            {formatNumber(event.review_average_rating, {
              minimumFractionDigits: 2, maximumFractionDigits: 2,
            })}
          </UI.Span>
          <UI.Span sc={{ color: 'gray.500', fontWeight: 300 }}>
            {' / '}
            {formatNumber(5, {
              minimumFractionDigits: 2, maximumFractionDigits: 2,
            })}
          </UI.Span>
        </UI.Div>
      </UI.Div>

      {event.review_aspect_counts.length > 0 && (
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('event_info.top_aspects')}
          </UI.H4>

          <UI.Card sc={{ outline: 'gray.200' }}>
            <UI.GridContainer sc={{ gutter: 0.25 }}>
              {event.review_aspect_counts.map((aspect) => (
                <UI.GridContainer
                  sc={{ columns: ['3fr 2fr 60px', '1fr 1fr 80px'], gutter: 0.25, alignVertical: 'center' }}
                  key={aspect.type}
                >
                  <UI.Div>
                    {t(`common:review_aspect_types.${aspect.type}`)}
                  </UI.Div>
                  <UI.Div>
                    <ProgressBar
                      count={aspect.count}
                      capacity={event.review_aspect_counts[0].count}
                      brand="secondary"
                    />
                  </UI.Div>
                  <UI.Div sc={{ color: 'gray.500', textAlign: 'right' }}>
                    {formatNumber(aspect.count)}
                    &times;
                  </UI.Div>
                </UI.GridContainer>
              ))}
            </UI.GridContainer>
          </UI.Card>
        </UI.Div>
      )}

      <UI.Div>
        <UI.H4 sc={{ mb: 2 }}>
          {t('event_info.participant_reviews')}
        </UI.H4>

        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {reviews.data.map((review) => (
            <UI.FadeIn key={review.id} style={{ minWidth: 0 }}>
              <ReviewCard review={review} />
            </UI.FadeIn>
          ))}

          {reviews.data.length < reviews.total && (
            <UI.FlexContainer sc={{ justifyContent: 'center' }}>
              <UI.Button
                onClick={() => showMoreReviews()}
                sc={{ borderRadius: 'xl', size: 'sm', outline: true }}
                disabled={loading}
              >
                <UI.Icon>
                  {loading ? <UI.Loader sc={{ size: 14, brand: 'gray.800' }} /> : <ArrowDown />}
                </UI.Icon>
                {' '}
                {t('event_info.show_more')}
              </UI.Button>
            </UI.FlexContainer>
          )}
        </UI.GridContainer>
      </UI.Div>
    </UI.GridContainer>
  );
};

interface ReviewCardProps {
  review: Review;
}

const ReviewCard = ({ review }: ReviewCardProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();

  return (
    <UI.Card sc={{ spacing: 'sm' }}>
      <UI.GridContainer sc={{ columns: '34px 1fr', gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div>
          <UI.ProfilePic sc={{ fit: 'cover', background: 'secondary', size: 34 }}>
            <span>
              <span>
                {review.name.charAt(0)}
              </span>
            </span>
          </UI.ProfilePic>
        </UI.Div>
        <UI.Div>
          <UI.Div sc={{ strong: true }}>
            {review.name}
          </UI.Div>
          <UI.Div sc={{ color: 'gray.500' }}>
            {t('event_info.participated_year', {
              year: review.event.start_date.substring(0, 4),
            })}
          </UI.Div>
        </UI.Div>
      </UI.GridContainer>
      <UI.FlexContainer sc={{ gap: 0.5, alignItems: 'center', wrap: 'wrap', mt: 2 }}>
        <RatingStars rating={review.rating} />
        <UI.Span sc={{ color: 'gray.500' }}>
          {formatDate(
            parseDate(review.created_at, { timezone: 'UTC' }),
            { format: 'display_compact_date' },
          )}
        </UI.Span>
      </UI.FlexContainer>
      {review.comment && (
        <UI.Div sc={{ mt: 2 }}>
          <ShowMoreToggle maxHeight={63} justify="start">
            <UI.NL2BR content={review.comment} />
          </ShowMoreToggle>
        </UI.Div>
      )}
    </UI.Card>
  );
};

interface RatingStarsProps {
  rating: number;
}

const RatingStars = ({ rating }: RatingStarsProps) => {
  const stars = [1, 2, 3, 4, 5];

  return (
    <UI.FlexContainer sc={{ small: true }}>
      {stars.map((star) => (
        <UI.Icon sc={{ mb: 0 }} key={star}>
          <Star fill={star <= rating ? 'currentColor' : 'none'} />
        </UI.Icon>
      ))}
    </UI.FlexContainer>
  );
};

export const FundraisingSection = () => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { locale, formatNumber, formatCurrency } = useLocale();

  const supportaLocale = config.supporta.supportedLocales.includes(locale) ? locale : Locale.en;

  const exclusiveCharity = event.charities.length === 1 && !event.allow_other_charity ? event.charities[0] : null;

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.H3>
        {t('event_info.fundraising')}
      </UI.H3>

      <UI.NavCard
        href={`${config.supporta.url}/${supportaLocale}/fundraisers/all/${event.id}`}
        target="_blank"
        rel="noopener noreferrer"
        sc={{ spacing: 'sm', background: 'gray.150/80', outline: 'gray.200' }}
        title={t('common:supporta')}
      >
        <UI.GridContainer
          sc={{ columns: '40px min-content min-content 1fr 18px', gutter: [0.75, 1], alignVertical: 'center' }}
        >
          <UI.Div>
            <UI.ProfilePic
              sc={{
                size: 40,
                background: !exclusiveCharity?.logo_url ? 'supporta_emerald' : 'white',
                color: 'white',
              }}
            >
              {exclusiveCharity?.logo_url ? (
                <img src={exclusiveCharity.logo_url} alt={exclusiveCharity.title} />
              ) : (
                <UI.Icon sc={{ size: 1.25 }}>
                  <SupportaSymbol />
                </UI.Icon>
              )}
            </UI.ProfilePic>
          </UI.Div>
          <UI.Div sc={{ noWrap: true }}>
            <UI.Div sc={{ fontSize: 2, fontWeight: 500 }}>
              {formatCurrency(event.funds_raised, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </UI.Div>
            <UI.Div sc={{ small: true }}>
              {t('event_info.raised')}
            </UI.Div>
          </UI.Div>
          <UI.Div sc={{ noWrap: true }}>
            <UI.Div sc={{ fontSize: 2, fontWeight: 500 }}>
              {formatNumber(event.fundraising_campaigns_count)}
            </UI.Div>
            <UI.Div sc={{ small: true }}>
              {t('event_info.fundraisers', { count: event.fundraising_campaigns_count })}
            </UI.Div>
          </UI.Div>
          <UI.Div sc={{ noWrap: true }}>
            {!exclusiveCharity && (
              <>
                <UI.Div sc={{ fontSize: 2, fontWeight: 500 }}>
                  {formatNumber(event.fundraising_charities_count)}
                </UI.Div>
                <UI.Div sc={{ small: true }}>
                  {t('event_info.charities', { count: event.fundraising_charities_count })}
                </UI.Div>
              </>
            )}
          </UI.Div>
          <UI.Div>
            <UI.Icon sc={{ size: 1.25, color: 'gray.500' }}>
              <ExternalLink />
            </UI.Icon>
          </UI.Div>
        </UI.GridContainer>
      </UI.NavCard>
    </UI.GridContainer>
  );
};

export const OrganizerSection = () => {
  const { t } = useTranslation();
  const { event } = useEvent();
  const { project } = event;

  const email = event.email || project.organisation_email;

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.H3>
        {t('organizer_info.organizer')}
      </UI.H3>

      <UI.GridContainer
        sc={{ columns: project.logo ? '42px 1fr' : '1fr', gutter: 0.5, alignVertical: 'center' }}
      >
        {project.logo && (
          <UI.ProfilePic sc={{ background: 'white', size: 42, borderRadius: 'md' }}>
            <img src={resizedImageUrl(project.logo.url, 300, 'webp')} alt={project.organisation_name} />
          </UI.ProfilePic>
        )}
        <UI.Div>
          <UI.H4>
            {project.organisation_name}
          </UI.H4>
          {event.website_url && (
            <UI.Div>
              <UI.A href={event.website_url} target="_blank" rel="noopener noreferrer" sc={{ secondary: true }}>
                {getUrlWithoutPrefix(event.website_url).split('/')[0]}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.A>
            </UI.Div>
          )}
          {!event.website_url && email && (
            <UI.Div>
              <UI.A href={`mailto:${email}`} sc={{ secondary: true }}>
                {email}
              </UI.A>
            </UI.Div>
          )}
        </UI.Div>
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

const RegisterButton = styled(UI.LinkButton)`
  ${({ theme }) => css`
    box-shadow: 0 3px 15px ${theme.transparentize(theme.colors.secondary[500], 0.5)}, inset 0 0 1px 1px ${theme.transparentize(theme.colors.secondary[400], 0.7)};
    backdrop-filter: blur(3px);
  `}
`;

export default AboutPage;
